import { useEffect, useMemo, useState } from 'react'
import { TriangleAlert, type LucideIcon } from 'lucide-react'
import classNames from 'classnames'
import debounce from 'lodash.debounce'

import Label from '@/elements/Label'
import { colors } from '@/utils/theme'

import './FormInput.scss'

type FormInputProps = {
  className?: string
  onChangeText?: (value: string) => void
  onChangeDebounced?: (value: string) => void
  label?: string
  instructions?: string
  style?: React.CSSProperties
  Icon?: LucideIcon
  customIcon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  rightAction?: React.ReactNode
  disabled?: boolean
  dirty?: boolean
  hasError?: boolean
  error?: string
  inputRef?: React.Ref<HTMLInputElement>
} & React.InputHTMLAttributes<HTMLInputElement>

function FormInput({
  className,
  onChange,
  onChangeText,
  onChangeDebounced,
  label,
  instructions,
  style,
  Icon,
  customIcon,
  iconPosition = 'left',
  rightAction,
  disabled = false,
  dirty = false,
  hasError = false,
  error,
  inputRef,
  ...inputDefaultProps
}: FormInputProps) {
  const [internalValue, setInternalValue] = useState(inputDefaultProps.value ?? '')

  useEffect(() => {
    setInternalValue(inputDefaultProps.value ?? '')
  }, [inputDefaultProps.value])

  const debouncedOnChange = useMemo(
    () => debounce((value: string) => {
      onChangeDebounced?.(value)
    }, 500),
    [onChangeDebounced]
  )

  return (
    <div
      style={style}
      className={
        classNames(
          'form-field',
          { 'icon-left': iconPosition === 'left', 'icon-right': iconPosition === 'right' },
          { 'has-right-action': !!rightAction },
          { dirty },
          { 'has-error': hasError },
          className
        )
      }>
      <Label htmlFor={inputDefaultProps.name} hidden={!label}>{label}</Label>
      <div className='field-inner'>
        {Icon && iconPosition === 'left' && (
          <Icon className='field-icon' size={18} />
        )}
        {customIcon && (
          <div className='field-icon field-icon-custom'>{customIcon}</div>
        )}
        <input
          id={inputDefaultProps.name}
          ref={inputRef}
          {...inputDefaultProps}
          onChange={e => {
            onChange?.(e)
            onChangeText?.(e.target.value)
            setInternalValue(e.target.value)
            debouncedOnChange(e.target.value)
          }}
          value={internalValue ?? ''}
          disabled={disabled}
        />
        {Icon && iconPosition === 'right' && (
          <Icon className='field-icon' size={18} />
        )}
        {rightAction && (
          <div className='field-action-right'>{rightAction}</div>
        )}
      </div>
      {instructions && (
        <span className='field-instructions'>
          {instructions}
        </span>
      )}
      {error && (
        <span className='field-error'>
          <TriangleAlert size={12} color={colors['red']} />
          {error}
        </span>
      )}
    </div>
  )
}

export default FormInput
